import {
  type Completion,
  type CompletionContext,
  type CompletionResult,
  snippetCompletion,
} from "@codemirror/autocomplete";
import { syntaxTree } from "@codemirror/language";
import {
  QUERY_OPERATORS,
  PIPELINE_STAGES,
  ACCUMULATOR_OPERATORS,
  type MongoOperator,
} from "./operators";
import { BSON_TYPES, BSON_KEYWORDS } from "./bsonTypes";
import {
  Object as NodeObject,
  Property,
  PropertyKey,
  Array as NodeArray,
  String as NodeString,
  Document,
  LineComment,
  BlockComment,
} from "./parser.terms";

// Derive SyntaxNode type from @codemirror/language's tree API,
// avoiding a direct @lezer/common dependency.
type SyntaxNode = ReturnType<ReturnType<typeof syntaxTree>["resolveInner"]>;

// ---------------------------------------------------------------------------
// Public config
// ---------------------------------------------------------------------------

export interface MongoCompletionConfig {
  fieldPaths?: string[];
  indexNames?: string[];
  operation?: string;
  variableNames?: string[];
  vaultSecretKeys?: string[];
  vaultConfigKeys?: string[];
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function mongoCompletion(
  config: MongoCompletionConfig,
): (ctx: CompletionContext) => CompletionResult | null {
  return (ctx) => complete(ctx, config);
}

// ---------------------------------------------------------------------------
// Core dispatcher
// ---------------------------------------------------------------------------

function complete(
  ctx: CompletionContext,
  config: MongoCompletionConfig,
): CompletionResult | null {
  const { state, pos } = ctx;
  const doc = state.doc.toString();

  // Never complete inside comments
  const tree = syntaxTree(state);
  const nodeAt = tree.resolveInner(pos, -1);
  if (nodeAt.type.id === LineComment || nodeAt.type.id === BlockComment) {
    return null;
  }

  // Never complete inside finished strings (cursor between quotes)
  if (isInsideString(nodeAt, pos)) {
    return null;
  }

  // --- Template variable completions (handle before tree-based logic) ---
  const tplResult = templateVariableCompletions(ctx, config, doc);
  if (tplResult) return tplResult;

  // --- Determine structural context ---
  const context = resolveContext(nodeAt, pos, doc);
  if (!context) {
    // Top-level value position (empty document or root value)
    if (nodeAt.type.id === Document || pos === 0) {
      return valueCompletions(ctx);
    }
    return null;
  }

  switch (context.kind) {
    case "key":
      return keyCompletions(ctx, config, context);
    case "value":
      return valueCompletions(ctx);
  }
}

// ---------------------------------------------------------------------------
// Context resolution
// ---------------------------------------------------------------------------

type CursorContext =
  | {
      kind: "key";
      parentObject: SyntaxNode;
      isPipelineElement: boolean;
      isAccumulatorContext: boolean;
      isTopLevel: boolean;
    }
  | { kind: "value" };

function resolveContext(
  node: SyntaxNode,
  pos: number,
  doc: string,
): CursorContext | null {
  let cur: SyntaxNode | null = node;

  while (cur) {
    // If we're on a PropertyKey, we're in key position
    if (cur.type.id === PropertyKey) {
      const prop = cur.parent;
      const obj = prop?.parent;
      if (obj && obj.type.id === NodeObject) {
        return makeKeyContext(obj, doc);
      }
    }

    // Inside a Property — determine key vs value by colon position
    if (cur.type.id === Property) {
      const colonPos = findColonInProperty(cur, doc);
      if (colonPos !== null && pos > colonPos) {
        return { kind: "value" };
      }
      const obj = cur.parent;
      if (obj && obj.type.id === NodeObject) {
        return makeKeyContext(obj, doc);
      }
    }

    // Directly in an Object (between properties or empty object)
    if (cur.type.id === NodeObject) {
      const textBefore = doc.slice(cur.from, pos);
      const lastColon = textBefore.lastIndexOf(":");
      const lastComma = textBefore.lastIndexOf(",");
      const lastBrace = textBefore.lastIndexOf("{");
      if (lastColon > lastComma && lastColon > lastBrace) {
        return { kind: "value" };
      }
      return makeKeyContext(cur, doc);
    }

    // In an Array → value position
    if (cur.type.id === NodeArray) {
      return { kind: "value" };
    }

    cur = cur.parent;
  }

  return null;
}

function makeKeyContext(
  obj: SyntaxNode,
  doc: string,
): Extract<CursorContext, { kind: "key" }> {
  return {
    kind: "key",
    parentObject: obj,
    isPipelineElement: isPipelineElement(obj),
    isAccumulatorContext: isInAccumulatorContext(obj, doc),
    isTopLevel: isTopLevelObject(obj),
  };
}

// ---------------------------------------------------------------------------
// Key-position completions
// ---------------------------------------------------------------------------

function keyCompletions(
  ctx: CompletionContext,
  config: MongoCompletionConfig,
  context: Extract<CursorContext, { kind: "key" }>,
): CompletionResult | null {
  const word = ctx.matchBefore(/[\w.$"']*/);
  if (!word) return null;

  let typed = word.text;
  let from = word.from;
  const isQuoted = typed.startsWith('"') || typed.startsWith("'");
  if (isQuoted) {
    typed = typed.slice(1);
    from += 1;
  }

  const options: Completion[] = [];

  // Choose operators based on structural context
  if (context.isPipelineElement) {
    addOperatorCompletions(options, PIPELINE_STAGES, "pipeline stage");
  } else if (context.isAccumulatorContext) {
    addOperatorCompletions(options, ACCUMULATOR_OPERATORS, "accumulator");
    addOperatorCompletions(options, QUERY_OPERATORS, "query operator");
  } else {
    addOperatorCompletions(options, QUERY_OPERATORS, "query operator");
  }

  // Field paths from schema
  if (config.fieldPaths?.length) {
    addFieldPathCompletions(options, config.fieldPaths, typed);
  }

  if (options.length === 0) return null;

  return { from, options, validFor: /^[\w.$]*$/ };
}

function addOperatorCompletions(
  options: Completion[],
  operators: MongoOperator[],
  detail: string,
): void {
  for (const op of operators) {
    if (op.snippet) {
      options.push(
        snippetCompletion(op.snippet, {
          label: op.name,
          type: "keyword",
          detail,
          info: op.description,
          boost: op.name === "$match" || op.name === "$group" ? 5 : 0,
        }),
      );
    } else {
      options.push({
        label: op.name,
        type: "keyword",
        detail,
        info: op.description,
      });
    }
  }
}

function addFieldPathCompletions(
  options: Completion[],
  fieldPaths: string[],
  typed: string,
): void {
  const dotIdx = typed.lastIndexOf(".");

  if (dotIdx >= 0) {
    // Dot prefix present — filter to matching sub-paths and suggest next segment
    const prefix = typed.slice(0, dotIdx + 1);
    const seen = new Set<string>();
    for (const fp of fieldPaths) {
      if (!fp.startsWith(prefix)) continue;
      const rest = fp.slice(prefix.length);
      const nextDot = rest.indexOf(".");
      const segment = nextDot >= 0 ? rest.slice(0, nextDot) : rest;
      const full = prefix + segment;
      if (seen.has(full)) continue;
      seen.add(full);
      options.push({
        label: full,
        type: "property",
        detail: "field",
        boost: 2,
      });
    }
  } else {
    // No dot — show top-level fields and full dotted paths for discovery
    const seen = new Set<string>();
    for (const fp of fieldPaths) {
      const dotPos = fp.indexOf(".");
      const topLevel = dotPos >= 0 ? fp.slice(0, dotPos) : fp;
      if (!seen.has(topLevel)) {
        seen.add(topLevel);
        options.push({
          label: topLevel,
          type: "property",
          detail: "field",
          boost: 2,
        });
      }
      if (dotPos >= 0 && !seen.has(fp)) {
        seen.add(fp);
        options.push({
          label: fp,
          type: "property",
          detail: "field path",
          boost: 1,
        });
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Value-position completions
// ---------------------------------------------------------------------------

function valueCompletions(ctx: CompletionContext): CompletionResult | null {
  const word = ctx.matchBefore(/[\w]*/);
  if (!word && !ctx.explicit) return null;
  const from = word?.from ?? ctx.pos;

  const options: Completion[] = [];

  // BSON constructors as snippets
  for (const bt of BSON_TYPES) {
    if (bt.hasArgs) {
      options.push(
        snippetCompletion(bt.snippet, {
          label: bt.name,
          type: "keyword",
          detail: "BSON type",
          info: bt.description,
        }),
      );
    }
  }

  // BSON keywords (MinKey, MaxKey, Infinity, NaN)
  for (const kw of BSON_KEYWORDS) {
    options.push({ label: kw, type: "keyword", detail: "BSON keyword" });
  }

  // Literals
  options.push(
    { label: "true", type: "keyword", detail: "boolean" },
    { label: "false", type: "keyword", detail: "boolean" },
    { label: "null", type: "keyword", detail: "null" },
  );

  return { from, options, validFor: /^[\w]*$/ };
}

// ---------------------------------------------------------------------------
// Template variable completions
// ---------------------------------------------------------------------------

function templateVariableCompletions(
  ctx: CompletionContext,
  config: MongoCompletionConfig,
  doc: string,
): CompletionResult | null {
  const { pos } = ctx;

  // Look backwards from cursor for opening {{
  const lookback = Math.min(pos, 200);
  const before = doc.slice(pos - lookback, pos);
  const openIdx = before.lastIndexOf("{{");
  if (openIdx < 0) return null;

  // Ensure no closing }} between {{ and cursor
  if (before.slice(openIdx + 2).includes("}}")) return null;

  const absOpenIdx = pos - lookback + openIdx;
  const insideText = doc.slice(absOpenIdx + 2, pos);

  // After "secret:" prefix → suggest vault secret keys
  if (insideText.startsWith("secret:")) {
    const from = absOpenIdx + 2 + "secret:".length;
    const options: Completion[] = (config.vaultSecretKeys ?? []).map((k) => ({
      label: k,
      type: "variable",
      detail: "vault secret",
    }));
    return options.length > 0 ? { from, options, validFor: /^[\w]*$/ } : null;
  }

  // After "config:" prefix → suggest vault config keys
  if (insideText.startsWith("config:")) {
    const from = absOpenIdx + 2 + "config:".length;
    const options: Completion[] = (config.vaultConfigKeys ?? []).map((k) => ({
      label: k,
      type: "variable",
      detail: "vault config",
    }));
    return options.length > 0 ? { from, options, validFor: /^[\w]*$/ } : null;
  }

  // Inside {{ with no or partial prefix
  const from = absOpenIdx + 2;
  const options: Completion[] = [];

  if (config.variableNames?.length) {
    for (const v of config.variableNames) {
      options.push({ label: v, type: "variable", detail: "template variable" });
    }
  }

  options.push(
    { label: "secret:", type: "variable", detail: "vault secret prefix" },
    { label: "config:", type: "variable", detail: "vault config prefix" },
  );

  return { from, options, validFor: /^[\w:]*$/ };
}

// ---------------------------------------------------------------------------
// Tree helpers
// ---------------------------------------------------------------------------

/** Object is a direct element of the top-level pipeline array. */
function isPipelineElement(obj: SyntaxNode): boolean {
  const parent = obj.parent;
  if (!parent || parent.type.id !== NodeArray) return false;
  return parent.parent?.type.id === Document;
}

/** Walk ancestors looking for a $group / $setWindowFields / $bucket property key. */
function isInAccumulatorContext(obj: SyntaxNode, doc: string): boolean {
  let cur: SyntaxNode | null = obj.parent;
  while (cur) {
    if (cur.type.id === Property) {
      const keyText = getPropertyKeyText(cur, doc);
      if (
        keyText === "$group" ||
        keyText === "$setWindowFields" ||
        keyText === "$bucket" ||
        keyText === "$bucketAuto"
      ) {
        return true;
      }
    }
    cur = cur.parent;
  }
  return false;
}

/** Object is a direct child of the Document root. */
function isTopLevelObject(obj: SyntaxNode): boolean {
  return obj.parent?.type.id === Document;
}

/** Scan for the colon separator inside a Property node. */
function findColonInProperty(prop: SyntaxNode, doc: string): number | null {
  const keyNode = prop.firstChild;
  if (!keyNode) return null;
  for (let i = keyNode.to; i < prop.to; i++) {
    if (doc[i] === ":") return i;
  }
  return null;
}

/** Cursor is between the quotes of a completed string literal. */
function isInsideString(node: SyntaxNode, pos: number): boolean {
  if (node.type.id !== NodeString) return false;
  return pos > node.from && pos < node.to;
}

/** Read the text content of a Property's key, stripping quotes. */
function getPropertyKeyText(prop: SyntaxNode, doc: string): string | null {
  const keyNode = prop.firstChild;
  if (!keyNode || keyNode.type.id !== PropertyKey) return null;
  let text = doc.slice(keyNode.from, keyNode.to);
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    text = text.slice(1, -1);
  }
  return text;
}
